import ClearIcon from "@mui/icons-material/Clear";
import { Grid } from "@mui/material";
import Flex from "@/components/Flex";
import Text from "@/components/Text";
import {
  CHART_COLORS,
  CHART_TYPOGRAPHY,
} from "@/components/app/provider/patient/chart";
import { SESSION_NOTE_LABEL } from "../group-visit-constant";
import { SessionCheckboxSection } from "../group-visit-types";
import { parseSelectedNames } from "../group-visit-utils";

export type SelectedOptionsSummaryProps = {
  sections: SessionCheckboxSection[];
  values: Record<string, string>;
  onRemove: (field: string, value: string) => void;
  disabled?: boolean;
};

/**
 * What a checkbox section currently holds, listed under its add button.
 *
 * @author Rajkumar Rathod
 */
const SelectedOptionsSummary = ({
  sections,
  values,
  onRemove,
  disabled,
}: SelectedOptionsSummaryProps) => {
  const visibleSections = sections
    .map((section) => {
      const selectedNames = parseSelectedNames(values[section.key]);
      return {
        section,
        selectedItems: section.items.filter((item) =>
          selectedNames.includes(item.name)
        ),
        note: values[section.noteKey] ?? "",
        selectedNames,
      };
    })
    .filter(({ selectedItems, note }) => selectedItems.length > 0 || !!note);

  if (visibleSections.length === 0) return null;

  return (
    <Flex flexDirection="column" gap={1} width="100%" mt={0.5}>
      {visibleSections.map(
        ({ section, selectedItems, selectedNames, note }) => (
          <Flex
            flexDirection="column"
            gap={0.25}
            width="100%"
            key={section.key}
          >
            <Grid container spacing={0.2}>
              {selectedItems.map((item) => (
                <Grid
                  key={item.name}
                  size={{
                    xs: 12,
                    sm: 6,
                    md: 4,
                  }}
                >
                  <Flex alignItems="center" gap={0.5}>
                    <Text
                      {...CHART_TYPOGRAPHY.body5}
                      c={CHART_COLORS.textDarker}
                    >
                      {`• ${item.label}`}
                    </Text>
                    {!disabled && (
                      <ClearIcon
                        color="error"
                        fontSize="small"
                        sx={{ cursor: "pointer", fontSize: "14px" }}
                        onClick={() =>
                          onRemove(
                            section.key,
                            selectedNames
                              .filter((name) => name !== item.name)
                              .join(",")
                          )
                        }
                      />
                    )}
                  </Flex>
                </Grid>
              ))}
            </Grid>

            {!!note && (
              <Text {...CHART_TYPOGRAPHY.body5} c={CHART_COLORS.textDarker}>
                <Text
                  component="span"
                  {...CHART_TYPOGRAPHY.body5}
                  fontWeight={CHART_TYPOGRAPHY.weight.medium}
                >
                  {`${SESSION_NOTE_LABEL.addNote}: `}
                </Text>
                {note}
              </Text>
            )}
          </Flex>
        )
      )}
    </Flex>
  );
};

export default SelectedOptionsSummary;
